/**
 * Pairing two lists of structured records, and working out what actually moved.
 *
 * Kept separate from the component because it is the part with a right answer: which record on the
 * left corresponds to which on the right, and — once paired — which of its members changed. The
 * component renders whatever this returns.
 */

/** One member of a record: a key, the name to show for it, and its value as a display string. */
export type RecordDiffMember = {
	/** Stable identity of the member, matched across the two sides. Never shown. */
	key: string;
	/** Already-translated (or deliberately verbatim) member name. */
	label: string;
	/** The member's value, pre-formatted by the caller and compared by exact string equality. */
	value: string;
};

/** One structured record on one side of the comparison — a break rule, an OT rule, a mapping row. */
export type DiffRecord = {
	/**
	 * The correspondence key. Records carrying the same `matchKey` on the two sides are PAIRED, which
	 * is what turns "everything was rewritten" into "one member of one rule moved".
	 *
	 * The caller chooses it (a rule's name, a mapping's external id) because only the caller knows
	 * which member identifies the record. It does NOT have to be unique: a side legitimately holding
	 * two records with the same key pairs them off one for one, in order, and the surplus on either
	 * side falls out as added or removed.
	 */
	matchKey: string;
	/** Already-translated heading for the record. */
	title: string;
	members: RecordDiffMember[];
};

/**
 * What happened to a record.
 *
 * `changed` is the state the whole component exists for: the record is on both sides, so the question
 * is not "what are all its values" but "which one member moved".
 */
export type RecordDiffState = "removed" | "added" | "changed" | "unchanged";

/** One member that differs between the two sides of a paired record. `null` = absent on that side. */
export type RecordMemberChange = {
	key: string;
	label: string;
	currentValue: string | null;
	incomingValue: string | null;
};

/** One rendered entry: a record and what became of it. */
export type PairedRecordDiff = {
	/** Unique per rendered entry — `matchKey` is not, by design (see `DiffRecord.matchKey`). */
	key: string;
	state: RecordDiffState;
	title: string;
	/** `removed` / `added`: that side's members, in full. Empty for a paired record. */
	members: RecordDiffMember[];
	/** `changed`: only the members that differ — the signal. */
	changedMembers: RecordMemberChange[];
	/** `changed` / `unchanged`: the members identical on both sides, for the collapsed remainder. */
	unchangedMembers: RecordDiffMember[];
};

/**
 * Reading order, and it is an editorial choice rather than the input's order: a rule list is a SET,
 * so its index carries no meaning, while "what is being deleted" is the one thing a reviewer must not
 * miss. Destruction first, then the records that moved, then the additions, then the ones that are
 * only here to prove they were considered.
 */
const STATE_ORDER: Record<RecordDiffState, number> = { removed: 0, changed: 1, added: 2, unchanged: 3 };

const EMPTY_MEMBERS: RecordDiffMember[] = [];
const EMPTY_CHANGES: RecordMemberChange[] = [];

/** The keys of both sides, current order first, with the incoming-only ones appended. */
function unionMemberKeys(current: RecordDiffMember[], incoming: RecordDiffMember[]): string[] {
	const seen = new Set(current.map((member) => member.key));
	return [...current.map((member) => member.key), ...incoming.filter((member) => !seen.has(member.key)).map((member) => member.key)];
}

/** Compare one paired record member by member. */
function comparePair(key: string, current: DiffRecord, incoming: DiffRecord): PairedRecordDiff {
	const currentByKey = new Map(current.members.map((member) => [member.key, member]));
	const incomingByKey = new Map(incoming.members.map((member) => [member.key, member]));
	const changedMembers: RecordMemberChange[] = [];
	const unchangedMembers: RecordDiffMember[] = [];

	for (const memberKey of unionMemberKeys(current.members, incoming.members)) {
		const before = currentByKey.get(memberKey);
		const after = incomingByKey.get(memberKey);
		if (before && after && before.value === after.value) {
			unchangedMembers.push(after);
			continue;
		}
		changedMembers.push({
			key: memberKey,
			// The incoming side names the member where it has one: it is the side being proposed, so a
			// renamed label should read as the proposal's, not the saved config's.
			label: after?.label ?? before?.label ?? memberKey,
			currentValue: before?.value ?? null,
			incomingValue: after?.value ?? null,
		});
	}

	return {
		key,
		state: changedMembers.length > 0 ? "changed" : "unchanged",
		// The incoming title for the same reason the incoming label wins above.
		title: incoming.title,
		members: EMPTY_MEMBERS,
		changedMembers,
		unchangedMembers,
	};
}

/**
 * Pair two lists of records by `matchKey` and classify each one.
 *
 * Pairing is by key only — never positional. Two lists whose names do not correspond are honestly a
 * set of removals beside a set of additions, and inventing a correspondence between "Rest AM" and
 * "Rest" would claim a member moved when in truth one rule went and another arrived.
 */
export function pairRecordSets(current: DiffRecord[], incoming: DiffRecord[]): PairedRecordDiff[] {
	const availableByKey = new Map<string, number[]>();
	incoming.forEach((record, index) => {
		const queue = availableByKey.get(record.matchKey);
		if (queue) queue.push(index);
		else availableByKey.set(record.matchKey, [index]);
	});

	const paired = new Set<number>();
	const results: PairedRecordDiff[] = current.map((record, index) => {
		const partnerIndex = availableByKey.get(record.matchKey)?.shift();
		if (partnerIndex === undefined) {
			return {
				key: `current-${index}`,
				state: "removed",
				title: record.title,
				members: record.members,
				changedMembers: EMPTY_CHANGES,
				unchangedMembers: EMPTY_MEMBERS,
			};
		}
		paired.add(partnerIndex);
		return comparePair(`current-${index}`, record, incoming[partnerIndex]);
	});

	incoming.forEach((record, index) => {
		if (paired.has(index)) return;
		results.push({
			key: `incoming-${index}`,
			state: "added",
			title: record.title,
			members: record.members,
			changedMembers: EMPTY_CHANGES,
			unchangedMembers: EMPTY_MEMBERS,
		});
	});

	// `sort` is stable, so within a state the input order survives.
	return results.sort((left, right) => STATE_ORDER[left.state] - STATE_ORDER[right.state]);
}

/** How many records ended in each state — the summary strip's figures. */
export function countRecordStates(pairs: PairedRecordDiff[]): Record<RecordDiffState, number> {
	const counts: Record<RecordDiffState, number> = { removed: 0, added: 0, changed: 0, unchanged: 0 };
	for (const pair of pairs) counts[pair.state] += 1;
	return counts;
}
